import {
  Component,
  Host,
  h,
  Element,
  Prop,
  State,
  Watch,
  Method,
  Event,
  EventEmitter,
} from '@stencil/core';

import dayjs, { Dayjs } from 'dayjs';
import {
  FiscalYearFormats,
  FiscalYearStarts,
  WeekStarts,
  VisibleRows,
  Resolution,
  visibleRows,
} from './types';
import isToday from 'dayjs/plugin/isToday';
dayjs.extend(isToday);
import updateLocale from 'dayjs/plugin/updateLocale';
dayjs.extend(updateLocale);
import localeData from 'dayjs/plugin/localeData';
dayjs.extend(localeData);

// The maximum number of rows that can be displayed.
const maximumRows = Math.max(...visibleRows);

function startOfQuarter(date: Dayjs, startOfFiscalYear: number = 0) {
  return date
    .startOf('month')
    .month(
      date.month() -
        startOfFiscalYear -
        ((date.month() - startOfFiscalYear) % 3) +
        startOfFiscalYear,
    );
}

function isStartOfQuarter(date: Dayjs, startOfFiscalYear: number = 0) {
  return date.startOf('month').isSame(date) && (date.month() - startOfFiscalYear) % 3 === 0;
}

function isEndOfQuarter(date: Dayjs, startOfFiscalYear: number = 0) {
  return date.endOf('month').isSame(date) && (date.month() - startOfFiscalYear + 2) % 3 === 0;
}

function fiscalQuarter(date: Dayjs, startOfFiscalYear: number = 0) {
  const monthIndex = (date.month() - startOfFiscalYear + 12) % 12;
  return Math.floor(monthIndex / 3 + 1);
}

function fiscalYear(date: Dayjs, startOfFiscalYear: number = 0) {
  const month = date.month();
  const year = date.year();
  const yearOffset = Math.floor((month - (startOfFiscalYear % 12 || 12)) / 12) + 1;
  return yearOffset + year;
}

interface DateObj {
  date: Dayjs;
  isToday: boolean;
  fiscalQuarter: number;
  fiscalYear: number;
  bounds: {
    left: number;
    right: number;
    width: number;
  };
  isStartOf: {
    range: boolean;
    week: boolean;
    month: boolean;
    quarter: boolean;
    fiscalYear: boolean;
    year: boolean;
  };
}

function buildDates({
  startDate,
  endDate,
  startFiscalYear = 0,
  weekStart = 0,
}: {
  startDate: Dayjs;
  endDate: Dayjs;
  startFiscalYear: number;
  weekStart: number;
}) {
  const numDays = endDate.diff(startDate, 'days') + 1;

  return Array.from(Array(numDays).keys()).reduce((acc, num, i, arr) => {
    const date = startDate.add(num, 'days');
    const key = date.format('YYYY-MM-DD');

    const left = (num / numDays) * 100;
    const right = ((num + 1) / numDays) * 100;
    const width = right - left;

    const value: DateObj = {
      date: date,
      isToday: date.isToday(),
      fiscalQuarter: fiscalQuarter(date, startFiscalYear),
      fiscalYear: fiscalYear(date, startFiscalYear),
      bounds: {
        left,
        right,
        width,
      },
      isStartOf: {
        range: num === 0,
        week: date.startOf('week').isSame(date),
        month: date.startOf('month').isSame(date),
        quarter: isStartOfQuarter(date, startFiscalYear),

        fiscalYear:
          isStartOfQuarter(date, startFiscalYear) && (date.month() - startFiscalYear) % 12 === 0,

        year: date.startOf('year').isSame(date),
      },
    };

    acc.set(key, value);
    return acc;
  }, new Map());
}

export interface AhaTimeline extends HTMLAhaTimelineElement {}

/**
 * A component which displays a timeline of dates.
 *
 * ## Public methods
 *
 *  * `getDate(date: string): DateObj | undefined` -- Returns the date object for the given date. The specified   date must be in `YYYY-MM-DD` format.
 *
 * ## Developer notes
 *
 * Both the `getDate` method and the `aha-timeline:change` event are provided to allow the developer to access the same information that the timeline uses to draw its day, week, month, quarter, and year boundaries.
 *
 * The `getDate` method allows the developer to query details about a single date. It is accessed via:
 *
 * ``` javascript
 * myAhaTimlineElem.getDate('2023-04-08')
 * ```
 *
 * and returns a rich date object with a number of useful items:
 *
 *
 * ```typescript
  DateObj {
    date: Dayjs;            // A DayJS object representing the date
    isToday: boolean;       // Whether or not today is this date
    fiscalQuarter: number;  // The fiscal quarter of the date
    fiscalYear: number;     // The fiscal year of the date
    bounds: {
      left: number;         // The left edge of the date in percent from the left side of the timeline
      right: number;        // The right edge of the date in percent from the left side of the timeline
      width: number;        // The width of the date in percent
    };
    isStartOf: {
      range: boolean;       // Whether or not this date is the first date in the timeline
      week: boolean;        // Whether or not this date is the start of a week
      month: boolean;       // Whether or not this date is the start of a month
      quarter: boolean;     // Whether or not this date is the start of a quarter
      fiscalYear: boolean;  // Whether or not this date is the start of a fiscal year
      year: boolean;        // Whether or not this date is the start of a calendar year
    };
  }
```
 *
  * The `aha-timeline:change` event is fired whenever the user interacts with the timeline, and provides the same information as the `getDate` method, but provides _all_ of the rich date objects for _all_ the dates in the timeline's span. This information is provided in the `detail` property of the event, and is a map of `DateObj` objects.
 */
@Component({
  tag: 'aha-timeline',
  styleUrl: 'aha-timeline.css',
  shadow: true,
})
export class AhaTimeline {
  @Element() host!: HTMLAhaTimelineElement;

  private innerElem?: HTMLElement;

  /** The initial starting date of the timeline. Must be in `YYYY-MM-DD` format. */
  @Prop({ reflect: true }) start: string = dayjs().format('YYYY-MM-DD');

  /** The final ending date of the timeline. Must be in `YYYY-MM-DD` format. */
  @Prop({ reflect: true }) end: string = dayjs(this.start).add(90, 'days').format('YYYY-MM-DD');

  /** The number of rows visible in the timeline. */
  @Prop({ reflect: true }) rows?: VisibleRows;

  /** The smallest possible date unit displayed in the bottom-most dateline row. Note that this is _not_ necessarily the size that _will be shown_, rather it just represents the _desired_ unit size for the bottom-most row; only rows which can fit without intersecting will actually be shown. */
  @Prop({ reflect: true }) resolution?: Resolution;

  private hideUnitRows() {
    if (this.innerElem) {
      const rows: HTMLElement[] = Array.from(this.innerElem.querySelectorAll('.row'));
      const indexOfUnit = rows.findIndex(
        row => row.dataset.resolution === this.resolution || row.dataset.unit === this.resolution,
      );

      rows.forEach((row, i) => {
        // An `inert` row is a row that cannot be shown under any circumstances.
        row.inert = i < indexOfUnit;
      });
    }
  }

  /** The month that the fiscal year starts. Must be from values `0` (January) to `11` (December). */
  @Prop({ reflect: true, attribute: 'start-fiscal-year' }) startFiscalYear: FiscalYearStarts = 0;

  /** The format of the fiscal year. Can be values of:
   *
   * * `FY Q` (returns "FY23 Q1")
   * * `Q FY` (returns "Q1 FY23")
   * * `YYYY Q` (returns "2023 Q1")
   * * `Q YYYY` (returns "Q1 2023").  */
  @Prop({ reflect: true, attribute: 'fiscal-year-format' }) fiscalYearFormat: FiscalYearFormats =
    'FY Q';

  /** The day on which the week starts. Can be `0` (Sunday) or `1` (Monday). */
  @Prop({ reflect: true, attribute: 'week-start' }) weekStart: WeekStarts = 0;

  @State() dates: any;

  @State() resolutions?: any;

  componentWillLoad() {
    this.buildTimeline();
  }

  componentDidLoad() {
    new ResizeObserver(entries => {
      this.checkOverlapping();
    }).observe(this.host);
  }

  private buildResolutions() {
    const resolutions: Map<string, any[]> = new Map([
      ['daysByName', []],
      ['days', []],
      ['daysByWeek', []],
      ['monthsByYear', []],
      ['months', []],
      ['quartersByYear', []],
      ['quarters', []],
      ['years', []],
    ]);

    this.dates.forEach((d: DateObj, key: string) => {
      const date = d.date;

      const { left: dayLeft, width: dayWidth } = this.getDimensions(d, 'day');

      resolutions.get('daysByName')?.push(
        <div style={{ left: dayLeft, width: dayWidth }}>
          <div>
            <span>{date.format('ddd')}</span>
            <span>{date.format('D')}</span>
          </div>
        </div>,
      );

      resolutions.get('days')?.push(
        <div style={{ left: dayLeft, width: dayWidth }}>
          <div>{date.format('D')}</div>
        </div>,
      );

      if (d.isStartOf.week) {
        let { left: weekLeft, width: weekWidth } = this.getDimensions(d, 'week');
        resolutions.get('daysByWeek')?.push(
          <div style={{ left: weekLeft, width: weekWidth }}>
            <div>{date.format('D')}</div>
          </div>,
        );
      }

      if (d.isStartOf.month || d.isStartOf.range) {
        let { left: monthLeft, width: monthWidth } = this.getDimensions(d, 'month');
        resolutions.get('monthsByYear')?.push(
          <div style={{ left: monthLeft, width: monthWidth }}>
            <div>
              <span>{date.format('MMM')}</span>
              <span>{date.format('YYYY')}</span>
            </div>
          </div>,
        );

        resolutions.get('months')?.push(
          <div style={{ left: monthLeft, width: monthWidth }}>
            <div>{date.format('MMM')}</div>
          </div>,
        );
      }

      if (d.isStartOf.quarter || d.isStartOf.range) {
        let { left: quarterLeft, width: quarterWidth } = this.getDimensions(d, 'quarter');
        resolutions.get('quartersByYear')?.push(
          <div style={{ left: quarterLeft, width: quarterWidth }}>
            <div>
              {this.formatQuarter(d.date).map(s => (
                <span>{s}</span>
              ))}
            </div>
          </div>,
        );
        resolutions.get('quarters')?.push(
          <div style={{ left: quarterLeft, width: quarterWidth }}>
            <div>Q{fiscalQuarter(d.date, this.startFiscalYear)}</div>
          </div>,
        );
      }

      if (d.isStartOf.year || d.isStartOf.range) {
        let { left: yearLeft, width: yearWidth } = this.getDimensions(d, 'year');
        resolutions.get('years')?.push(
          <div style={{ left: yearLeft, width: yearWidth }}>
            <div>{date.format('YYYY')}</div>
          </div>,
        );
      }
    });
    return resolutions;
  }

  @Watch('start')
  @Watch('end')
  @Watch('resolution')
  @Watch('startFiscalYear')
  @Watch('fiscalYearFormat')
  @Watch('weekStart')
  @Watch('rows')
  private buildTimeline() {
    dayjs.updateLocale(dayjs.locale(), {
      weekStart: this.weekStart,
    });

    this.dates = buildDates({
      startDate: dayjs(this.start),
      endDate: dayjs(this.end),
      startFiscalYear: this.startFiscalYear || 0,
      weekStart: this.weekStart || 0,
    });

    this.resolutions = this.buildResolutions();

    // Wait a tick so that the resolutions can be flushed to the DOM before we check for overlapping boxes.
    requestAnimationFrame(() => {
      this.hideUnitRows();
      this.checkOverlapping();
      this.changeEvent.emit({ host: this, dates: this.dates });
    });
  }

  /**
   * Triggers when the timeline's information has changed.
   */
  @Event({
    eventName: 'aha-timeline:change',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  private formatQuarter(date: dayjs.Dayjs) {
    const YYYY = fiscalYear(date, this.startFiscalYear);
    const FY = `FY${YYYY.toString().slice(2)}`;
    const Q = `Q${fiscalQuarter(date, this.startFiscalYear)}`;

    switch (this.fiscalYearFormat) {
      case 'FY Q':
        return [FY, Q];
      case 'Q FY':
        return [Q, FY];
      case 'YYYY Q':
        return [YYYY, Q];
      case 'Q YYYY':
      default:
        return [Q, YYYY];
    }
  }

  get duration() {
    return dayjs(this.end).diff(this.start, 'days') + 1;
  }

  private checkOverlapping() {
    if (this.innerElem) {
      const visibleRows = new Set();
      let counter = 1;
      this.innerElem.querySelectorAll<HTMLElement>(':scope .row').forEach(row => {
        // An `inert` row is a row that cannot be shown under any circumstances. This is usually because it is a smaller date unit resolution than the `resolution` prop allows.
        // A `hidden` row is a row that cannot be seen, either because it is inert (unavailable for any reason), or because the zoom level is such that the row has dates which intersect, and hence should not be shown.
        row.hidden = false;

        if (row.inert) {
          // Any inert row is also hidden.
          row.hidden = true;
        } else {
          // NOTE: We intentionally ignore the last child because it can easily overflow its parent, depending on where the timeline ends. Instead, we simply set overflow:hidden on last children.
          const rowHasIntersections = Array.from(
            row.querySelectorAll<HTMLElement>(':scope > *:not(:last-child)'),
          ).some(node => {
            node.hidden = false;
            const child = node.children[0];
            const { left: nodeLeft, right: nodeRight } = node.getBoundingClientRect();
            const { left: childLeft, right: childRight } = child.getBoundingClientRect();

            if (childLeft < nodeLeft || childRight > nodeRight) {
              // If the first child overflows its parent, then it's likely that the start date of the timeline is such that there is not much difference between the first and second values (ex. timeline starts on March 28). In this case, we just hide the first child entirely, as there's not enough room for it to give us any useful context.
              if (node.matches(':first-child')) {
                node.hidden = true;
                return false;
              }
              return true;
            }
            return false;
          });
          row.hidden =
            rowHasIntersections ||
            visibleRows.has(row.dataset.unit) ||
            visibleRows.size >= (this.rows ?? maximumRows);

          if (!rowHasIntersections) visibleRows.add(row.dataset.unit);

          if (row.hidden) {
            delete row.dataset.row;
          } else {
            row.dataset.row = counter.toString();
            counter++;
          }
        }
      });

      const numVisibleRows = this.innerElem.querySelectorAll<HTMLElement>(
        ':scope .row:not([hidden])',
      ).length;
      this.innerElem.dataset.visibleRows = numVisibleRows.toString();
    }
  }

  private getDimensions(d: DateObj, unit: string | dayjs.UnitType) {
    const left = d.bounds.left;
    let next;

    if (unit === 'quarter') {
      next = this.dates.get(
        startOfQuarter(d.date, this.startFiscalYear).add(3, 'months').format('YYYY-MM-DD'),
      );
    } else {
      // @ts-ignore
      next = this.dates.get(d.date.startOf(unit).add(1, unit).format('YYYY-MM-DD'));
    }

    const right = next ? next.bounds.left : 100;
    const width = right - left;
    return { left: `${left}%`, width: `${width}%` };
  }

  /** A method which will return all relevant information for a specific date. */
  // eslint-disable-next-line @stencil/own-methods-must-be-private
  public getDate(date: string) {
    return (
      this.dates.get(date) ||
      console.error(`Specified date "${date}" is not present on this timeline.`)
    );
  }

  render() {
    return (
      <Host>
        <div id="timeline" ref={el => (this.innerElem = el)}>
          {/*
            NOTE: Each row has a "unit" that corresponds to the date unit type which this row represents. No more than one row from any given "unit" can be displayed at a time (assuming that now intersects occur, preventing the row from being shown at all).

            Each row _may_ also have a "resolution", though typically this is not needed. The row's resolution corresponds to the "resolution" prop of the `<aha-timeline>` component, and represents the minimum data unit which can be displayed.
          */}
          <div class="row" data-unit="day">
            {this.resolutions.get('daysByName')}
          </div>
          <div class="row" data-unit="day">
            {this.resolutions.get('days')}
          </div>
          <div class="row" data-unit="day" data-resolution="week">
            {this.resolutions.get('daysByWeek')}
          </div>
          <div class="row" data-unit="month">
            {this.resolutions.get('monthsByYear')}
          </div>
          <div class="row" data-unit="month">
            {this.resolutions.get('months')}
          </div>
          <div class="row" data-unit="quarter">
            {this.resolutions.get('quartersByYear')}
          </div>
          <div class="row" data-unit="quarter">
            {this.resolutions.get('quarters')}
          </div>
          <div class="row" data-unit="year">
            {this.resolutions.get('years')}
          </div>
        </div>
      </Host>
    );
  }
}
